import { useState, useEffect } from "react";
import { useNotification } from "../NotificationContext";
import "./SecondCameraQRModal.css";

const SecondCameraQRModal = ({ interviewId, onProceed, onClose }) => {
  const { notify } = useNotification();
  const [qrB64, setQrB64] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQR = async () => {
      try {
        const baseUrl = process.env.REACT_APP_REST_BASE_URL || "http://localhost:8000/api";
        const response = await fetch(`${baseUrl}/second_camera/one`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ interview_id: interviewId }),
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} - ${response.statusText}`);
        }

        const data = await response.json();
        setQrB64(data.qr_b64);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch QR code:", err);
        setError("Failed to generate QR code. Please try again.");
        setLoading(false);
        notify("error", "Unable to load second camera QR. Check your connection and retry.");
      }
    };

    if (interviewId) fetchQR();
  }, [interviewId, notify]);

  const handleProceed = () => {
    onProceed();
  };

  return (
    <div className="tt-second-camera-overlay">
      <section className="tt-second-camera-modal" role="dialog" aria-modal="true" aria-labelledby="second-camera-title">
        <button type="button" onClick={onClose} className="tt-second-camera-close" aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>

        <header className="tt-second-camera-head">
          <span className="tt-second-camera-tile" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="6" y="2" width="12" height="20" rx="2.6" fill="currentColor" />
              <circle cx="12" cy="10" r="3.2" fill="#fff" />
              <circle cx="12" cy="10" r="1.3" fill="currentColor" />
              <rect x="10" y="17.5" width="4" height="1.6" rx=".8" fill="#fff" />
            </svg>
          </span>
          <h2 id="second-camera-title">Second Camera Setup</h2>
        </header>

        {loading ? (
          <div className="tt-second-camera-loading" role="status" aria-live="polite">
            <span className="tt-second-camera-loader" aria-hidden="true" />
            <p>Generating QR code...</p>
          </div>
        ) : error ? (
          <div className="tt-second-camera-error" role="alert">
            <span className="tt-second-camera-error-icon" aria-hidden="true">!</span>
            <p>{error}</p>
            <button type="button" onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : (
          <>
            <p className="tt-second-camera-sub">
              Scan this QR code with your phone's camera to enable the second camera for recording.
            </p>

            <div className="tt-second-camera-qr-wrap">
              <div className="tt-second-camera-qr">
                <img src={qrB64} alt="QR Code for Second Camera" />
                <span className="tt-second-camera-corner top-left" aria-hidden="true" />
                <span className="tt-second-camera-corner top-right" aria-hidden="true" />
                <span className="tt-second-camera-corner bottom-left" aria-hidden="true" />
                <span className="tt-second-camera-corner bottom-right" aria-hidden="true" />
                <span className="tt-second-camera-scan" aria-hidden="true" />
              </div>
            </div>

            <aside className="tt-second-camera-note" role="note">
              <span className="tt-second-camera-info" aria-hidden="true">i</span>
              <span className="tt-second-camera-divider" aria-hidden="true" />
              <p>
                <strong>Important:</strong> After scanning, your phone will open your camera. Ensure your phone's camera is enabled. Place your phone at a 45&#176; angle for the best side view.
              </p>
            </aside>

            <button type="button" onClick={handleProceed} className="tt-second-camera-continue">
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 8V5a2 2 0 0 1 2-2h3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <rect x="7" y="7" width="4" height="4" rx=".6" stroke="currentColor" strokeWidth="1.6" />
                <rect x="13" y="7" width="4" height="4" rx=".6" stroke="currentColor" strokeWidth="1.6" />
                <rect x="7" y="13" width="4" height="4" rx=".6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M13 13h1.5M16.5 13H17v1.5M13 16.5V17h1.5M16.5 16.5H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              I've Scanned the QR - Continue
            </button>
          </>
        )}
      </section>
    </div>
  );
};

export default SecondCameraQRModal;
